import type { ButtonProps as MantineButtonProps} from '@mantine/core';
import {Button as MantineButton} from '@mantine/core';
import type {ElementType, MouseEventHandler} from 'react';

/**
 * Primary action button — thin Mantine `Button` wrapper with Proact defaults
 * (`variant="filled"`, `size="md"`).
 * @registry Default filled button, size md, for primary/standalone actions.
 *
 * Ported from `library-m/components/buttons/DefaultButton`. Pass
 * `component={Link} to="/path"` for a button that navigates (a real link).
 */
export interface ButtonProps extends Omit<MantineButtonProps, 'onClick'> {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: 'submit' | 'reset' | 'button';
  /** Polymorphic element (e.g. `component={Link} to="/path"`) so navigation renders a real link. Mirrors `PageHeader.Action`. */
  component?: ElementType;
  /** Destination when `component` is a routing `Link`. */
  to?: string;
}

export const Button = ({
  variant = 'filled',
  size = 'md',
  children,
  component,
  to,
  ...props
}: ButtonProps) => {
  // Generic ElementType doesn't satisfy Mantine's component-keyed overload union.
  const polymorphicProps = {component, to} as Record<string, unknown>;
  return (
    <MantineButton
      {...polymorphicProps}
      variant={variant}
      size={size}
      {...props}>
      {children}
    </MantineButton>
  );
};
